import * as yup from 'yup';
import { getRequest, putRequest } from '../../../services/httpService';

// These enum values for MenstrualHistory are used in the form and the API
export type AmenorrheaTypeEnum = 'PRIMARY' | 'SECONDARY';
export type BlankEnum = '';
export type NullEnum = 'NULL';
export type MedicationEnum = 'CONTRACEPTIVE_PILLS' | 'DUPHASTON' | 'NORETHISTERONE_5_MG' | 'MICROGYNON_30' | 'CLOMID_50' | 'LETROZOLE_2_5_MG' | 'GONAL_F_75' | 'GONAL_F_300' | 'GONAL_F_450' | 'GONAL_F_900' | 'MENOPURE_1200' | 'MENOPURE_900' | 'MENOPURE_300' | 'MENOPURE_150' | 'BEMFOLA_75' | 'BEMFOLA_150' | 'BEMFOLA_225' | 'BEMFOLA_300' | 'BEMFOLA_450' | 'OVALEP_F_75' | 'OVALEP_F_225' | 'OVALEP_F_300' | 'OVALEP_F_450' | 'OVALEP_F_900' | 'OVALEP_F_1200' | 'CETROTIDE_250_MCG' | 'FRYMADEL_250_MCG' | 'OVITRELLE_250_MCG' | 'GONASY_500_MCG' | 'BUSERALINE_5_5_ML' | 'UTROGESTAN_200_MG_PESSARY' | 'CYCLOGEST_400_MG_PESSARY' | 'LUBION_INJ_25' | 'CLEXENE_20' | 'CLEXENE_40' | 'INHEXA_20' | 'INHEXA_40' | 'CABERGOLINE_250_MCG' | 'PRESDNISOLONE_5_MG' | 'INTRALIPID_INFUSION' | 'CO_ENZYME_10' | 'DHEA' | 'E2_TABLETS_2_MG' | 'PROGYNOVA_2_MG' | 'EVROL_PATCHES_50_MCG' | 'AUGMENTINE_625_MG' | 'EVROL_PATCHES_75_MCG' | 'EVROL_PATCHES_100_MCG' | 'AZETHROMYCINE_1G' | 'BUSCOPAN_10_MG' | 'DOXYCYCLINE_500_MG' | 'METRONIDAZOL_1GM_PESSARY' | 'VOLTAROL_100_MG_PESSARY';
export type MenstrualRegularityEnum = 'REGULAR' | 'REGULARLY_IRREGULAR' | 'IRREGULARLY_IRREGULAR';
export type MenstrualFlowEnum = 'NORMAL' | 'SCANTY' | 'MODERATE' | 'HEAVY';
export type BowelChangesEnum = 'NO' | 'YES' | 'ALWAYS' | 'SOMETIMES' | 'RECENTLY' | 'IN_THE_PAST';
export type DysmenorrheaEnum = 'ABSENT' | 'MILD' | 'MODERATE' | 'SEVERE';

export type MasterItem = {
  id: number;
  name: string;
  code: string;
};

export type DropdownOption = {
  label: string
  value: string;
  related_name?: string
};



// MenstrualHistory interface which defines the shape of the MenstrualHistory object.
// Based on this interface formik makes all decisions and validations
export interface MenstrualHistory {
  menarche_age?: number | null;
  last_menstrual_period: string | null;
  has_amenorrhea: boolean|null;
  amenorrhea_type?: AmenorrheaTypeEnum | BlankEnum | NullEnum | null;
  has_medication_to_regulate_period: boolean | null;
  medication?: MedicationEnum | BlankEnum | NullEnum | null;
  cycle_duration?: number | null;
  menstruation_duration?: number | null;
  menstrual_regularity?: MenstrualRegularityEnum | BlankEnum | NullEnum | null;
  menstrual_flow?: MenstrualFlowEnum | BlankEnum | NullEnum | null;
  menstrual_flow_comment?: string | null;
  bowel_changes?: BowelChangesEnum | BlankEnum | NullEnum | null;
  dysmenorrhea?: DysmenorrheaEnum | BlankEnum | NullEnum | null;
  has_intermenstrual_bleeding: boolean|null;
  intermenstrual_bleeding_comment?: string | null;
  general_remark?: string | null;
}

// This is the initial state of the MenstrualHistory object
// We should fill all mandatory fields
// If a field is not mandatory, we can set it to null
export const initialMenstrualHistory: MenstrualHistory = {
  menarche_age: null,
  last_menstrual_period: null,
  has_amenorrhea: null,
  amenorrhea_type: null,
  has_medication_to_regulate_period: null,
  medication: null,
  cycle_duration: null,
  menstruation_duration: null,
  menstrual_regularity: null,
  menstrual_flow: null,
  menstrual_flow_comment: null,
  bowel_changes: null,
  dysmenorrhea: null,
  has_intermenstrual_bleeding: null,
  intermenstrual_bleeding_comment: null,
  general_remark: null,
};


// Export a function to get the validation schema with translations
// Accept t as an argument instead of using useTranslation inside
export function MenstrualHistoryValidationSchema(t: (key: string) => string) {
  return yup.object({
    menarche_age: yup
      .number()
      .min(8, t('menarche_age_min'))
      .max(18, t('menarche_age_max'))
      .nullable(),
    last_menstrual_period: yup
      .date()
      .typeError(t('last_menstrual_period_type'))
      .nullable()
      .required(t('last_menstrual_period_required'))
      .max(new Date(), t('last_menstrual_period_max')),
    has_amenorrhea: yup
      .boolean()
      .nullable(),

    // We define that amenorrhea_type is an optional field, and it can be one of the values in the array.
    // We are adding an empty string to the array to allow the user to select nothing.
    // If you give wrong values in array, typescript will throw error based on AmenorrheaTypeEnum type.
    amenorrhea_type: yup
      .mixed<AmenorrheaTypeEnum | BlankEnum | NullEnum>()
      .nullable(),
    has_medication_to_regulate_period: yup
      .boolean()
      .nullable(),
    medication: yup
      .mixed<MedicationEnum | BlankEnum | NullEnum>()
      .nullable(),
    cycle_duration: yup
      .number()
      .min(10, t('cycle_duration_min'))
      .max(60, t('cycle_duration_max'))
      .required(t('cycle_duration_required')),
    menstruation_duration: yup
      .number()
      .min(2, t('menstruation_duration_min'))
      .max(7, t('menstruation_duration_max'))
      .required(t('menstruation_duration_required')),
    menstrual_regularity: yup
      .mixed<MenstrualRegularityEnum | BlankEnum | NullEnum>()
      .required(t('menstrual_regularity_required')),
    menstrual_flow: yup
      .mixed<MenstrualFlowEnum | BlankEnum | NullEnum>()
      .required(t('menstrual_flow_required')),
    menstrual_flow_comment: yup
      .string()
      .max(100, t('menstrual_flow_comment_max'))
      .nullable(),
    bowel_changes: yup
      .mixed<BowelChangesEnum | BlankEnum | NullEnum>()
      .nullable(),
    dysmenorrhea: yup
      .mixed<DysmenorrheaEnum | BlankEnum | NullEnum>()
      .nullable(),
    has_intermenstrual_bleeding: yup
      .boolean()
      .nullable(),
    intermenstrual_bleeding_comment: yup
      .string()
      .max(100, t('intermenstrual_bleeding_comment_max'))
      .nullable(),
    general_remark: yup
      .string()
      .max(100, t('general_remark_max'))
      .nullable(),
  });
}


// These are the values that are used in the dropdowns in the form for Amenorrhea Type field
// If you give wrong values in array, typescript will throw error based on AmenorrheaTypeEnum type.
export const AmenorrheaTypeMenuItems: { value: AmenorrheaTypeEnum | BlankEnum; label: string }[] = [
  { value: 'PRIMARY', label: 'Primary' },
  { value: 'SECONDARY', label: 'Secondary' },
];

// These are the values that are used in the dropdowns in the form for Menstrual Regularity field
// If you give wrong values in array, typescript will throw error based on MenstrualRegularityEnum type.
export const MenstrualRegularityMenuItems: {value: MenstrualRegularityEnum | BlankEnum; label: string}[] = [
  { value: 'REGULAR', label: 'Regular' },
  { value: 'REGULARLY_IRREGULAR', label: 'Regularly Irregular' },
  { value: 'IRREGULARLY_IRREGULAR', label: 'Irregularly Irregular' },
];

// These are the values that are used in the dropdowns in the form for Menstrual Flow field
// If you give wrong values in array, typescript will throw error based on MenstrualFlowEnum type.
export const MenstrualFlowMenuItems: {value: MenstrualFlowEnum | BlankEnum; label: string}[] = [
  { value: 'NORMAL', label: 'Normal' },
  { value: 'SCANTY', label: 'Scanty' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'HEAVY', label: 'Heavy' },
];

// These are the values that are used in the dropdowns in the form for Bowel Changes field
// If you give wrong values in array, typescript will throw error based on BowelChangesEnum type.
export const BowelChangesMenuItems: {value: BowelChangesEnum | BlankEnum; label: string}[] = [
  { value: 'NO', label: 'No' },
  { value: 'YES', label: 'Yes' },
  { value: 'ALWAYS', label: 'Always' },
  { value: 'SOMETIMES', label: 'Sometimes' },
  { value: 'RECENTLY', label: 'Recently' },
  { value: 'IN_THE_PAST', label: 'In the Past' },
];

// These are the values that are used in the dropdowns in the form for Dysmenorrhea field
// If you give wrong values in array, typescript will throw error based on DysmenorrheaEnum type.
export const DysmenorrheaMenuItems: {value: DysmenorrheaEnum | BlankEnum; label: string}[] = [
  { value: 'ABSENT', label: 'Absent' },
  { value: 'MILD', label: 'Mild' },
  { value: 'MODERATE', label: 'Moderate' },
  { value: 'SEVERE', label: 'Severe' },
];

// This method is used to generate the URL for the MenstrualHistory API
export function generateMenstrualHistoryURL(id:number): string {
  return `patients/${id}/menstrual-history/`;
}

// This method is used to update the MenstrualHistory object in the API
export function updateMenstrualHistory(id: number, data:MenstrualHistory): Promise<MenstrualHistory> {
  return putRequest(generateMenstrualHistoryURL(id),data, { responseType: 'json' });
}

// This method is used to fetch the MenstrualHistory object from the API
export function getMenstrualHistory(id: number): Promise<MenstrualHistory> {
  return getRequest(generateMenstrualHistoryURL(id), { responseType: 'json' });
}
// Master integration for Menstrual History



export function fetchMedicalHistoryMasterData(type: string): Promise<MenstrualHistory> {
  const url = `clinics/configuration/?module_type=history&type=${type}`;
  return getRequest(url, { responseType: 'json' });
}





//reusable utility function to simplify your map logic for dropdown data
export function mapDropdownOptions(data: unknown): DropdownOption[] {
  return Array.isArray(data)
    ? (data as MasterItem[]).map((item) => ({
      label: item.name,
      value: item.code,
    }))
    : [];
}





